import { computed } from 'vue';

/**
 * Pagination logic composable for the front context.
 * Encapsulates all derived pagination values from raw inputs.
 *
 * @param {import('vue').Ref<number>} currentPageRef  - reactive current page (1-based)
 * @param {import('vue').Ref<number>} totalRef        - reactive total number of items
 * @param {import('vue').Ref<number>} perPageRef      - reactive items per page
 * @param {object}                   [options]
 * @param {number}                   [options.windowSize=2] - pages visible either side of current
 */
export function usePagination(currentPageRef, totalRef, perPageRef, options = {}) {
    const { windowSize = 2 } = options;

    /** Total number of pages */
    const totalPages = computed(() => Math.ceil(totalRef.value / perPageRef.value) || 1);

    /** Clipped current page (safe for display / guard logic) */
    const currentPage = computed(() => Math.min(Math.max(currentPageRef.value, 1), totalPages.value));

    /** Array of page numbers visible in the paginator */
    const visiblePages = computed(() => {
        const start = Math.max(1, currentPage.value - windowSize);
        const end   = Math.min(totalPages.value, currentPage.value + windowSize);
        const pages = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    });

    /** 1-based index of the first item shown on the current page */
    const rangeFrom = computed(() => {
        if (totalRef.value === 0) return 0;
        return (currentPage.value - 1) * perPageRef.value + 1;
    });

    /** 1-based index of the last item shown on the current page */
    const rangeTo = computed(() => Math.min(currentPage.value * perPageRef.value, totalRef.value));

    /**
     * Validate and return a safe page number.
     * Returns null when the requested page is already current or out of range.
     *
     * @param {number} page
     * @returns {number|null}
     */
    function resolvePage(page) {
        if (page < 1 || page > totalPages.value) return null;
        if (page === currentPage.value) return null;
        return page;
    }

    return {
        totalPages,
        currentPage,
        visiblePages,
        rangeFrom,
        rangeTo,
        resolvePage,
    };
}
